import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { returnService } from "../../services/returnService";
import { toast } from "react-toastify";

// Async Thunks for Admin Return Operations

export const fetchAdminReturns = createAsyncThunk(
  "returnsAdmin/fetchAdminReturns",
  async (
    { page = 1, limit = 10, status = "", search = "" },
    { rejectWithValue }
  ) => {
    try {
      console.log("fetchAdminReturns thunk called with:", {
        page,
        limit,
        status,
        search,
      });
      const data = await returnService.getAdminReturns(
        page,
        limit,
        status,
        search
      );
      console.log("fetchAdminReturns success, data:", data);
      return data; // { returns: [], pagination: {} }
    } catch (error) {
      console.error("fetchAdminReturns error:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch admin returns";
      toast.error(`Error fetching returns: ${message}`);
      return rejectWithValue(message);
    }
  }
);

export const fetchAdminReturnDetail = createAsyncThunk(
  "returnsAdmin/fetchAdminReturnDetail",
  async (returnId, { rejectWithValue }) => {
    if (!returnId) {
      console.error("fetchAdminReturnDetail: returnId is undefined or null");
      return rejectWithValue("Return ID is required");
    }

    try {
      console.log(
        `fetchAdminReturnDetail: Fetching details for return ID ${returnId}`
      );
      const data = await returnService.getReturnById(returnId);
      console.log("fetchAdminReturnDetail: Success", data);
      return data;
    } catch (error) {
      console.error("fetchAdminReturnDetail: Error", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch return detail";
      toast.error(`Error fetching return detail: ${message}`);
      return rejectWithValue(message);
    }
  }
);

export const submitReturnReview = createAsyncThunk(
  "returnsAdmin/submitReturnReview",
  async (
    { returnId, itemDecisions, globalAdminNotes },
    { rejectWithValue }
  ) => {
    try {
      const data = await returnService.submitReturnReview(
        returnId,
        itemDecisions,
        globalAdminNotes
      );
      toast.success("Return review submitted successfully!");
      return data; // Updated return object
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to submit return review";
      toast.error(`Error submitting review: ${message}`);
      return rejectWithValue(message);
    }
  }
);

export const markItemsReceived = createAsyncThunk(
  "returnsAdmin/markItemsReceived",
  async ({ returnId, itemReceipts, globalAdminNotes }, { rejectWithValue }) => {
    try {
      const data = await returnService.markItemsReceivedAdmin(
        returnId,
        itemReceipts,
        globalAdminNotes
      );
      toast.success("Items marked as received successfully!");
      return data; // Updated return object
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to mark items received";
      toast.error(`Error marking items received: ${message}`);
      return rejectWithValue(message);
    }
  }
);

export const processRefund = createAsyncThunk(
  "returnsAdmin/processRefund",
  async ({ returnId, refundMethod, adminNotes }, { rejectWithValue }) => {
    try {
      const data = await returnService.processRefundAdmin(
        returnId,
        refundMethod,
        adminNotes
      );
      toast.success("Refund processed successfully!");
      return data; // Updated return object
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to process refund";
      toast.error(`Error processing refund: ${message}`);
      return rejectWithValue(message);
    }
  }
);

// Initial State
const initialState = {
  returns: [], // List of returns for admin view
  pagination: null,
  currentReturn: null, // Holds the details of the return being viewed/edited
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  actionStatus: "idle", // For tracking individual action status (review, receive, refund)
  actionError: null,
};

// Returns Slice
const returnsAdminSlice = createSlice({
  name: "returnsAdmin",
  initialState,
  reducers: {
    resetReturnState: (state) => {
      state.status = "idle";
      state.error = null;
      state.actionStatus = "idle";
      state.actionError = null;
      state.currentReturn = null;
    },
    resetCurrentReturn: (state) => {
      state.currentReturn = null;
      state.actionStatus = "idle";
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Admin Returns
      .addCase(fetchAdminReturns.pending, (state) => {
        console.log("fetchAdminReturns.pending - Setting state to loading");
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAdminReturns.fulfilled, (state, action) => {
        console.log(
          "fetchAdminReturns.fulfilled - Setting returns data",
          action.payload
        );
        console.log(
          "Returns array type:",
          Array.isArray(action.payload.returns)
        );
        console.log("Returns array length:", action.payload.returns?.length);
        console.log("First return item:", action.payload.returns?.[0]);
        state.status = "succeeded";
        state.returns = action.payload.returns;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAdminReturns.rejected, (state, action) => {
        console.log(
          "fetchAdminReturns.rejected - Setting error",
          action.payload
        );
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch Admin Return Detail
      .addCase(fetchAdminReturnDetail.pending, (state) => {
        state.status = "loading";
        state.currentReturn = null;
        state.error = null;
      })
      .addCase(fetchAdminReturnDetail.fulfilled, (state, action) => {
        console.log(
          "fetchAdminReturnDetail.fulfilled - Got return data:",
          action.payload
        );
        state.status = "succeeded";
        state.currentReturn = action.payload;
      })
      .addCase(fetchAdminReturnDetail.rejected, (state, action) => {
        console.error(
          "fetchAdminReturnDetail.rejected - Error:",
          action.payload
        );
        state.status = "failed";
        state.error = action.payload;
      })
      // Submit Return Review
      .addCase(submitReturnReview.pending, (state) => {
        state.actionStatus = "loading";
        state.actionError = null;
      })
      .addCase(submitReturnReview.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        state.currentReturn = action.payload; // Update current return with result
        // Optionally update the list if needed, or rely on refetch
      })
      .addCase(submitReturnReview.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.actionError = action.payload;
      })
      // Mark Items Received
      .addCase(markItemsReceived.pending, (state) => {
        state.actionStatus = "loading";
        state.actionError = null;
      })
      .addCase(markItemsReceived.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        state.currentReturn = action.payload;
      })
      .addCase(markItemsReceived.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.actionError = action.payload;
      })
      // Process Refund
      .addCase(processRefund.pending, (state) => {
        state.actionStatus = "loading";
        state.actionError = null;
      })
      .addCase(processRefund.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        state.currentReturn = action.payload;
      })
      .addCase(processRefund.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.actionError = action.payload;
      });
  },
});

export const { resetReturnState, resetCurrentReturn } =
  returnsAdminSlice.actions;

export default returnsAdminSlice.reducer;
